import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { formatDate } from "@/utils/timezone";
import { formatCurrency } from "@/utils/formatters";
import ColoredCurrency from "../ColoredCurrency";
import api from "@/api";

/**
 * TradeHistory - Reusable component for displaying trade history
 * Fetches its own data from /accounts/{accountId}/transactions
 *
 * Used in two contexts:
 * 1. AccountView - Full account history with filters and pagination
 * 2. PerformanceAnalytics - Period-specific trades (filtered, no pagination)
 */
const TradeHistory = ({
  // Required
  accountId,

  // Initial filters (optional - for PerformanceAnalytics date ranges)
  initialFilter = "ALL",
  initialTicker = "",
  initialStartDate = "",
  initialEndDate = "",

  // Display options
  showFilters = false,
  showPagination = false,
  title = "Trade History",
  emptyMessage = "No transactions found",

  // Optional callback for intel badge clicks
  onIntelClick = null,
}) => {
  // State
  const [transactions, setTransactions] = useState([]);
  const [intel, setIntel] = useState([]);
  const [intelCountByTicker, setIntelCountByTicker] = useState({});
  const [loading, setLoading] = useState(false);
  const [transactionFilter, setTransactionFilter] = useState(initialFilter);
  const [transactionTicker, setTransactionTicker] = useState(initialTicker);
  const [transactionStartDate, setTransactionStartDate] =
    useState(initialStartDate);
  const [transactionEndDate, setTransactionEndDate] = useState(initialEndDate);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [sortColumn, setSortColumn] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  const itemsPerPage = 50;

  // Fetch transactions and intel
  const fetchTransactions = async () => {
    if (!accountId) return;

    try {
      setLoading(true);
      const params = { page: currentPage, limit: itemsPerPage };

      if (transactionFilter !== "ALL") {
        params.action = transactionFilter;
      }
      if (transactionStartDate) {
        params.start_date = transactionStartDate;
      }
      if (transactionEndDate) {
        params.end_date = transactionEndDate;
      }
      if (transactionTicker) {
        params.ticker = transactionTicker;
      }

      // Fetch both transactions and intel in parallel
      const [transactionsResponse, intelResponse] = await Promise.all([
        api.get(`/accounts/${accountId}/transactions`, { params }),
        api.get(`/intel`, {
          params: {
            ticker: transactionTicker || undefined,
          },
        }),
      ]);

      const fetchedTransactions = transactionsResponse.data.transactions;
      const fetchedIntel = intelResponse.data.items || intelResponse.data || [];

      setTransactions(fetchedTransactions);
      setIntel(fetchedIntel);
      setTotalItems(transactionsResponse.data.total);
      setCurrentPage(transactionsResponse.data.page);
      setTotalPages(transactionsResponse.data.total_pages);

      // Calculate intel counts by ticker
      const counts = {};
      fetchedIntel.forEach((intelItem) => {
        if (intelItem.tickers && Array.isArray(intelItem.tickers)) {
          intelItem.tickers.forEach((ticker) => {
            counts[ticker] = (counts[ticker] || 0) + 1;
          });
        }
      });
      setIntelCountByTicker(counts);
    } catch (error) {
      console.error("Error fetching data:", error);
      setTransactions([]);
      setIntel([]);
      setIntelCountByTicker({});
    } finally {
      setLoading(false);
    }
  };

  // Sync state with props when initial dates change (for PerformanceAnalytics period switching)
  useEffect(() => {
    if (initialStartDate !== transactionStartDate) {
      setTransactionStartDate(initialStartDate);
      setCurrentPage(1); // Reset to first page when dates change
    }
    if (initialEndDate !== transactionEndDate) {
      setTransactionEndDate(initialEndDate);
      setCurrentPage(1); // Reset to first page when dates change
    }
  }, [initialStartDate, initialEndDate]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchTransactions();
  }, [
    accountId,
    currentPage,
    transactionFilter,
    transactionStartDate,
    transactionEndDate,
    transactionTicker,
  ]);

  const handleFilterChange = (updates) => {
    if (updates.filter !== undefined) setTransactionFilter(updates.filter);
    if (updates.ticker !== undefined) setTransactionTicker(updates.ticker);
    if (updates.startDate !== undefined)
      setTransactionStartDate(updates.startDate);
    if (updates.endDate !== undefined) setTransactionEndDate(updates.endDate);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setTransactionTicker("");
    setTransactionStartDate("");
    setTransactionEndDate("");
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column and default to descending
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  // Sort transactions based on current sort state
  const sortedTransactions = [...transactions].sort((a, b) => {
    let aVal = a[sortColumn];
    let bVal = b[sortColumn];

    // Handle null/undefined values
    if (aVal === null || aVal === undefined) aVal = "";
    if (bVal === null || bVal === undefined) bVal = "";

    // Convert to numbers for numeric columns
    if (["shares", "price", "amount", "realized_pnl"].includes(sortColumn)) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    }

    // Compare values
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const getSortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  return (
    <div>
      {/* Header with Filters */}
      {showFilters && (
        <div className="section-content">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              <h3 className="section-title">{title}</h3>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                flexWrap: "wrap",
              }}
            >
              {/* Type Filter */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <label
                  className="text-muted"
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Type:
                </label>
                <select
                  value={transactionFilter}
                  onChange={(e) =>
                    handleFilterChange({ filter: e.target.value })
                  }
                  style={{
                    padding: "0.5rem 2rem 0.5rem 0.75rem",
                    border: "1px solid var(--form-input-border)",
                    borderRadius: "4px",
                    background: "var(--account-type-bg)",
                    color: "var(--title-color)",
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <option value="ALL">All</option>
                  <option value="BUY">Buy</option>
                  <option value="SELL">Sell</option>
                  <option value="DIVIDEND">Dividend</option>
                  <option value="CONTRIBUTION">Contribution</option>
                  <option value="CASH_IN">Cash In</option>
                  <option value="SHARES_IN">Shares In</option>
                  <option value="WITHDRAWAL">Withdrawal</option>
                </select>
              </div>

              {/* Stock/Ticker Filter */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <label
                  className="text-muted"
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  Stock:
                </label>
                <input
                  type="text"
                  value={transactionTicker}
                  onChange={(e) =>
                    handleFilterChange({ ticker: e.target.value.toUpperCase() })
                  }
                  placeholder="e.g. AAPL"
                  style={{
                    padding: "0.5rem 0.75rem",
                    border: "1px solid var(--form-input-border)",
                    borderRadius: "4px",
                    background: "var(--account-type-bg)",
                    color: "var(--title-color)",
                    fontSize: "0.875rem",
                    outline: "none",
                    width: "100px",
                  }}
                />
              </div>

              {/* Date Range Filter */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <label
                  className="text-muted"
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  From:
                </label>
                <input
                  type="date"
                  value={transactionStartDate}
                  onChange={(e) =>
                    handleFilterChange({ startDate: e.target.value })
                  }
                  style={{
                    padding: "0.5rem 0.75rem",
                    border: "1px solid var(--form-input-border)",
                    borderRadius: "4px",
                    background: "var(--account-type-bg)",
                    color: "var(--title-color)",
                    fontSize: "0.875rem",
                    outline: "none",
                  }}
                />
                <label
                  className="text-muted"
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "500",
                  }}
                >
                  To:
                </label>
                <input
                  type="date"
                  value={transactionEndDate}
                  onChange={(e) =>
                    handleFilterChange({ endDate: e.target.value })
                  }
                  style={{
                    padding: "0.5rem 0.75rem",
                    border: "1px solid var(--form-input-border)",
                    borderRadius: "4px",
                    background: "var(--account-type-bg)",
                    color: "var(--title-color)",
                    fontSize: "0.875rem",
                    outline: "none",
                  }}
                />
                {(transactionStartDate ||
                  transactionEndDate ||
                  transactionTicker) && (
                  <button
                    onClick={handleClearFilters}
                    className="btn btn-secondary"
                    style={{
                      padding: "0.5rem 0.75rem",
                      fontSize: "0.875rem",
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Title only (if no filters) */}
      {!showFilters && title && (
        <div className="section-content">
          <h3 className="section-title">{title}</h3>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div
          className="text-muted"
          style={{
            padding: "2rem",
            textAlign: "center",
          }}
        >
          Loading transactions...
        </div>
      ) : transactions.length === 0 ? (
        /* Empty State */
        <div
          className="text-muted"
          style={{
            padding: "2rem",
            textAlign: "center",
          }}
        >
          {emptyMessage}
        </div>
      ) : (
        /* Transactions Table */
        <div style={{ padding: "1.5rem", overflowX: "auto" }}>
          <table className="performance-table data-table">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("date")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Date{getSortIndicator("date")}
                </th>
                <th
                  onClick={() => handleSort("action")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Action{getSortIndicator("action")}
                </th>
                <th
                  onClick={() => handleSort("ticker")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Ticker{getSortIndicator("ticker")}
                </th>
                <th
                  onClick={() => handleSort("shares")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Shares{getSortIndicator("shares")}
                </th>
                <th
                  onClick={() => handleSort("price")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Price{getSortIndicator("price")}
                </th>
                <th
                  onClick={() => handleSort("amount")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  Amount{getSortIndicator("amount")}
                </th>
                <th
                  onClick={() => handleSort("realized_pnl")}
                  style={{ cursor: "pointer", userSelect: "none" }}
                >
                  P&L{getSortIndicator("realized_pnl")}
                </th>
                <th>Intel</th>
              </tr>
            </thead>
            <tbody>
              {sortedTransactions.map((transaction, index) => {
                const intelCount = transaction.ticker
                  ? intelCountByTicker[transaction.ticker] || 0
                  : 0;

                return (
                  <tr key={transaction.id || index}>
                    <td>{formatDate(transaction.date, "M/d/yy")}</td>
                    <td>
                      <strong>{transaction.action}</strong>
                    </td>
                    <td>
                      {transaction.ticker ? (
                        <Link
                          to={`/ticker/${transaction.ticker}?accountId=${accountId}`}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <strong style={{ cursor: "pointer" }}>
                            {transaction.ticker}
                          </strong>
                        </Link>
                      ) : (
                        <strong>-</strong>
                      )}
                    </td>
                    <td>{transaction.shares || "-"}</td>
                    <td>
                      {transaction.price
                        ? formatCurrency(transaction.price)
                        : "-"}
                    </td>
                    <td>
                      <ColoredCurrency value={transaction.amount} />
                    </td>
                    <td>
                      {transaction.realized_pnl !== null &&
                      transaction.realized_pnl !== undefined ? (
                        <ColoredCurrency value={transaction.realized_pnl} />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {intelCount > 0 && onIntelClick ? (
                        <button
                          onClick={() => onIntelClick(transaction.ticker)}
                          style={{
                            background: "#4299e1",
                            color: "white",
                            border: "none",
                            borderRadius: "12px",
                            padding: "0.25rem 0.625rem",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            transition: "all 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#3182ce";
                            e.currentTarget.style.transform = "scale(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#4299e1";
                            e.currentTarget.style.transform = "scale(1)";
                          }}
                          title={`View ${intelCount} intel item${
                            intelCount > 1 ? "s" : ""
                          } for ${transaction.ticker}`}
                        >
                          👁️ {intelCount}
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {showPagination && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "1.5rem",
                padding: "1rem",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <div className="text-muted" style={{ fontSize: "0.875rem" }}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} transactions
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: "0.5rem 1rem",
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    background: currentPage === 1 ? "#f8fafc" : "white",
                    color: currentPage === 1 ? "#cbd5e0" : "#4a5568",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    fontWeight: "500",
                  }}
                >
                  Previous
                </button>
                <div
                  style={{
                    padding: "0.5rem 1rem",
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    background: "#f8fafc",
                    color: "#4a5568",
                  }}
                >
                  Page {currentPage} of {totalPages}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: "0.5rem 1rem",
                    border: "1px solid #e2e8f0",
                    borderRadius: "4px",
                    background:
                      currentPage === totalPages ? "#f8fafc" : "white",
                    color: currentPage === totalPages ? "#cbd5e0" : "#4a5568",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                    fontWeight: "500",
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TradeHistory;
